import Modal from '@components/modal/modal';
import { ModalType, PricingType, QuotationResponseType } from '../../../enums';
import { SaveQuoteResponse } from '../../../type';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOrder } from '@hooks/useorder';

import '../../../styles/configure/quoteorderbar.scss';
import { CUSTOMISE_SEARCH } from '@navigation/constants';
import { useAppSelector, useAppDispatch } from '@hooks/statehooks';
import { useVehicle } from '@hooks/usevehicle';
import { AvailableOption } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/availableoption';
import { StartOrder } from 'type';
import { QuoteOrderBarProps } from 'interface';
import { Vehicle } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicle';

import ActionButton from '@components/actionbutton';
import CustomiseButton from '../banner/customisebutton';
import Typography from '@components/typography';
import {
  personalContractHire,
  cookieName,
  siteWidePersonalBusinessToggleSelector,
} from '../../../constants';
import { usePricing } from '@hooks/usepricing';
import { updateVehiclePricingType } from '@store/reducers/vehicleinformation';
import { createCookie } from '../../../utils/cookiehelper';
import { useSelectedOptions } from '@hooks/useselectedoptions';

const QuoteOrderBar: React.FC<QuoteOrderBarProps> = ({
  showCustomise,
  modalSettings,
}) => {
  const { saveToShowroom, startOrder } = useOrder();
  const {
    getVehicleInfo,
    vehicleDetails,
    canCustomise,
    getFuelType,
    getCustomisationValid,
  } = useVehicle();
  const { updateAllPricing } = usePricing();
  const { getNumberOfExtrasSelected, getTotalCostOfExtrasSelected } =
    useSelectedOptions();
  const dispatch = useAppDispatch();

  let navigate = useNavigate();

  const priceState = useAppSelector((state) => state.vehiclePrice);
  const vehicleState = useAppSelector((state) => state.vehicleInformation);
  const optionsState = useAppSelector((state) => state.selectedOptions);
  const searchParams = useAppSelector((state) => state.vehicleSearchParams);
  const isBch = vehicleState.pricingType === PricingType.Business;
  const productType = isBch ? 'BCH' : 'PCH';

  const customBchModalClassName = 'modal-content__bch-qualify';

  const [canOrder, setCanOrder] = useState<boolean>(false);
  // Logic to check if all mandatory options are selected *before* calling services
  useEffect(() => {
    getCustomisationValid(
      PricingType.Personal,
      searchParams.optionsSelected
    )?.then((isValid) => {
      if (vehicleState.stockVehicle === true || isValid === true) {
        // Set true if options are already valid or is a stick vehicle with no options
        return setCanOrder(true);
      } else {
        return setCanOrder(false);
      }
    });
  }, [searchParams]);

  useEffect(() => {
    const getSelectedExterior: AvailableOption =
      optionsState.colours.paint.find(
        (paint) => paint.checked
      ) as AvailableOption;

    const getSelectedInterior: AvailableOption = optionsState.colours.trim.find(
      (trim) => trim.checked
    ) as AvailableOption;

    const vehicleType = vehicleState.model.split(' ');
    const stockType = vehicleState.stockVehicle ? 'In-Stock' : 'Factory';
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'configLoad',
      page_category: 'Configure',
      page_name: 'Lease Config',
      vehicle: {
        make: vehicleState.make,
        model: vehicleState.model,
        derivative: vehicleState.type,
        id: vehicleState.id,
        fuel_type: getFuelType(),
        transmission: vehicleState.transmission,
        no_of_doors: vehicleState.doors,
        no_of_seats: vehicleState.seats,
        vehicle_type: vehicleType[vehicleType.length - 1],
        stock_type: stockType,
        exterior_colour: getSelectedExterior?.title,
        interior_colour: getSelectedInterior?.title,
      },
      lease: {
        product_type: productType,
        lead_time: vehicleState.stockLeadTime,
        lease_term: searchParams.duration,
        annual_mileage: searchParams.yearly_mileage,
        initial_payment: vehicleState.initialPayment?.default,
        monthly_payment: vehicleState.pricing?.price,
        processing_fee: vehicleState.pricing?.processingFee,
        excess_mileage_cost: vehicleState.pricing?.excessMileCharge,
        maintenance_cost: searchParams.include_maintenance
          ? priceState.pricing?.maintCost
          : 0,
        no_of_extras: getNumberOfExtrasSelected(),
        cost_of_extras: getTotalCostOfExtrasSelected(),
      },
    });
  }, []);

  const showModal = (
    type: ModalType,
    modalButtons?: JSX.Element,
    customClassName?: string
  ) => {
    modalSettings.setModalType(type);

    if (modalButtons) {
      modalSettings.setButtons(modalButtons);
    }

    if (customClassName) {
      modalSettings.setClassName(customClassName as string);
    }

    modalSettings.toggle();
  };

  const fireBchModalEvent = (eventLabel: string) => {
    window.dataLayer?.push({
      event: 'bch_popup',
      category: 'BCH Confirmation Popup',
      action: 'click',
      label: eventLabel,
    });
  };

  const confirmBchOrder = async (redirectUrl: string) => {
    fireBchModalEvent('Accept');
    window.location.assign(redirectUrl);
  };

  const revertQuoteToPch = () => {
    fireBchModalEvent('Decline');

    const element = document.getElementById(
      siteWidePersonalBusinessToggleSelector
    );

    if (element) {
      (element as HTMLInputElement).checked = false;
    }

    createCookie(cookieName, personalContractHire);

    getVehicleInfo(
      () => updateAllPricing(PricingType.Personal),
      PricingType.Personal
    );

    dispatch(updateVehiclePricingType(PricingType.Personal));

    modalSettings.closeModal();
  };

  const bchModalbuttons = (redirectUrl: string) => (
    <>
      <button
        onClick={revertQuoteToPch}
        className="
          modal-content__body-buttons-button
          modal-content__body-buttons-button--alternative"
        data-testid="no-bch-qualify-button"
      >
        <Typography variant="caption" size="rg" weight="semi-bold">
          No
        </Typography>
      </button>
      <ActionButton
        onClick={() => confirmBchOrder(redirectUrl)}
        data-cy="yes-bch-qualify-button"
        data-testid="yes-bch-qualify-button"
        className="modal-content__body-buttons-button
          modal-content__body-buttons-button--primary"
        colour="alternative"
        name="startorder"
      >
        <Typography
          variant="caption"
          size="rg"
          weight="semi-bold"
          colour="alternative"
        >
          Yes
        </Typography>
      </ActionButton>
    </>
  );

  const setModalContent = (
    result: QuotationResponseType,
    saveToShortlist: boolean,
    redirectUrl?: string
  ) => {
    switch (result) {
      case QuotationResponseType.mustLogin:
        showModal(
          saveToShortlist
            ? ModalType.registerLoginShortlist
            : ModalType.registerLoginStartOrder
        );
        break;
      case QuotationResponseType.activateEmail:
        showModal(ModalType.verifyEmail);
        break;
      case QuotationResponseType.UnknownError:
        console.error('Error in setModalContent');
        break;
      case QuotationResponseType.Success:
        showModal(ModalType.quoteSaved);
        break;
      case QuotationResponseType.invalidQuote:
        showModal(ModalType.invalidQuote);
        break;
      case QuotationResponseType.bchQualify:
        showModal(
          ModalType.bchQualify,
          bchModalbuttons(redirectUrl as string),
          customBchModalClassName
        );
        break;
    }
  };

  const saveQuoteStartDataLayerEvent = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'saveQuoteStart',
      lease: {
        product_type: productType,
        lead_time: vehicleState.stockLeadTime,
        lease_term: searchParams.duration,
        annual_mileage: searchParams.yearly_mileage,
        initial_payment: searchParams.initial_payment,
        monthly_payment: priceState.pricing?.price,
        processing_fee: priceState.pricing?.processingFee,
        excess_mileage_cost: priceState.pricing?.excessMileCharge,
        maintenance_cost: searchParams.include_maintenance
          ? priceState.pricing?.maintCost
          : 0,
        no_of_extras: getNumberOfExtrasSelected(),
        cost_of_extras: getTotalCostOfExtrasSelected(),
      },
    });
  };

  const saveQuoteDataLayerEvent = (quoteId: string) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'saveQuote',
      lease: {
        quote_id: quoteId,
        product_type: productType,
        lead_time: vehicleState.stockLeadTime,
        lease_term: searchParams.duration,
        annual_mileage: searchParams.yearly_mileage,
        initial_payment: searchParams.initial_payment,
        monthly_payment: priceState.pricing?.price,
        processing_fee: priceState.pricing?.processingFee,
        excess_mileage_cost: priceState.pricing?.excessMileCharge,
        maintenance_cost: searchParams.include_maintenance
          ? priceState.pricing?.maintCost
          : 0,
        no_of_extras: getNumberOfExtrasSelected(),
        cost_of_extras: getTotalCostOfExtrasSelected(),
      },
    });
  };

  const saveQuote = () => {
    saveQuoteStartDataLayerEvent();
    return new Promise(async (resolve, reject) => {
      saveToShowroom()
        .then((result: SaveQuoteResponse) => {
          setModalContent(result.data.modalTypeId, true);
          resolve('success');
          saveQuoteDataLayerEvent(result.data.quoteId);
        })
        .catch((err: QuotationResponseType) => {
          setModalContent(err, true);
          reject('error');
        });
    });
  };

  const continueQuoteStartDataLayerEvent = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'continueQuotestart',
      lease: {
        product_type: productType,
        lead_time: vehicleState.stockLeadTime,
        lease_term: searchParams.duration,
        annual_mileage: searchParams.yearly_mileage,
        initial_payment: searchParams.initial_payment,
        monthly_payment: priceState.pricing?.price,
        processing_fee: priceState.pricing?.processingFee,
        excess_mileage_cost: priceState.pricing?.excessMileCharge,
        maintenance_cost: searchParams.include_maintenance
          ? priceState.pricing?.maintCost
          : 0,
        no_of_extras: getNumberOfExtrasSelected(),
        cost_of_extras: getTotalCostOfExtrasSelected(),
      },
    });
  };

  const continueQuoteDataLayerEvent = (quoteId: string) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'continueQuote',
      lease: {
        quote_id: quoteId,
        product_type: productType,
        lead_time: vehicleState.stockLeadTime,
        lease_term: searchParams.duration,
        annual_mileage: searchParams.yearly_mileage,
        initial_payment: searchParams.initial_payment,
        monthly_payment: priceState.pricing?.price,
        processing_fee: priceState.pricing?.processingFee,
        excess_mileage_cost: priceState.pricing?.excessMileCharge,
        maintenance_cost: searchParams.include_maintenance
          ? priceState.pricing?.maintCost
          : 0,
        no_of_extras: getNumberOfExtrasSelected(),
        cost_of_extras: getTotalCostOfExtrasSelected(),
      },
    });
  };

  const doStartOrder = async () => {
    continueQuoteStartDataLayerEvent();
    return new Promise(async (resolve, reject) => {
      startOrder()
        .then((result: StartOrder) => {
          if (!result.quoteValid) {
            setModalContent(QuotationResponseType.invalidQuote, false);
            return;
          }

          if (result.configValid) {
            continueQuoteDataLayerEvent(result.quoteId);
            if (isBch) {
              setModalContent(
                QuotationResponseType.bchQualify,
                false,
                result.redirectPath
              );
            } else {
              window.location.assign(result.redirectPath);
            }
          } else {
            navigate(CUSTOMISE_SEARCH(searchParams), {
              state: {
                vehicle: { ...(vehicleDetails as Vehicle) },
                searchParams: { ...searchParams },
              },
            });
          }
          resolve('success');
        })
        .catch((err: QuotationResponseType) => {
          setModalContent(err, false);
          reject('error');
        });
    });
  };

  return (
    <>
      <div
        className={`quoteorderbar ${
          showCustomise ? 'quoteorderbar--open' : ''
        }`}
      >
        <ActionButton
          onClick={saveQuote}
          className="quoteorderbar__savequote"
          data-cy="sq"
          data-testid="sq"
          spinnerProps={{ colour: '#000000' }}
          name="savequote"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 18 16"
            width="18px"
            height="16px"
          >
            <path
              fill="#212121"
              d="m16.6 8.5-6.8 7.03c-.44.46-1.16.46-1.6 0L1.4 8.51a5.06 5.06 0 0 1 .32-7.3c1.91-1.65 4.83-1.35 6.59.46l.69.71.7-.71a4.84 4.84 0 0 1 6.58-.46 5.06 5.06 0 0 1 .32 7.3Z"
            />
          </svg>
          <div>Save</div>
        </ActionButton>
        {canCustomise() && (
          <CustomiseButton
            className="quoteorderbar__customise"
            changeOptions={canOrder}
          />
        )}
        {canOrder && (
          <ActionButton
            onClick={doStartOrder}
            data-cy="so"
            data-testid="so"
            className="quoteorderbar__startorder"
            colour="alternative"
            name="startorder"
          >
            Continue
          </ActionButton>
        )}

        <Modal
          visible={modalSettings.visible}
          data-cy="modal-quoteorderbar"
          content={modalSettings.content}
          toggle={modalSettings.toggle}
          modalType={modalSettings.modalType}
          className={modalSettings.className}
        />
      </div>
    </>
  );
};

export default QuoteOrderBar;
